// =============================================================================
// Origin UI — src/pages/requests/MyRequestsPage.tsx
// Dashboard showing all requests created by the current user.
// Displays status, submission date, effective date, and quick actions.
// =============================================================================

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { requestApi } from '@/services/requestApi';
import { useAuth } from '@/hooks/useAuth';
import { StatusBadge, LoadingSpinner, EmptyState, PageHeader, Card, ErrorBanner } from '@/components/common';
import type { RequestListItem } from '@/types/api';
import styles from './requests.module.css';

export function MyRequestsPage() {
  const [requests, setRequests] = useState<RequestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const navigate = useNavigate();
  const { roles } = useAuth();

  useEffect(() => {
    loadRequests();
  }, []);

  async function loadRequests() {
    try {
      setLoading(true);
      const data = await requestApi.list();
      setRequests(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }

  const filtered = statusFilter === 'all'
    ? requests
    : requests.filter(r => r.status === statusFilter);

  const statusCounts: Record<string, number> = {};
  requests.forEach(r => {
    statusCounts[r.status] = (statusCounts[r.status] || 0) + 1;
  });

  if (loading) return <LoadingSpinner message="Loading your requests..." />;

  return (
    <div>
      <PageHeader
        title="My Requests"
        subtitle={`${requests.length} total request${requests.length !== 1 ? 's' : ''}`}
        action={
          roles.isHRAdmin ? (
            <Link to="/requests/new" className={styles.createBtn}>
              + New Request
            </Link>
          ) : undefined
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* Status filter pills */}
      <div className={styles.filterBar}>
        <button
          className={`${styles.filterPill} ${statusFilter === 'all' ? styles.filterActive : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          All ({requests.length})
        </button>
        {['Draft', 'Submitted', 'Returned', 'Approved', 'TasksInProgress', 'Completed', 'Cancelled'].map(s => {
          const count = statusCounts[s] || 0;
          if (count === 0) return null;
          return (
            <button
              key={s}
              className={`${styles.filterPill} ${statusFilter === s ? styles.filterActive : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'TasksInProgress' ? 'In Progress' : s} ({count})
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon="📋"
            title="No requests yet"
            message={roles.isHRAdmin
              ? "Create your first onboarding, offboarding, or department transfer request to get started."
              : "You have no requests. Onboarding, offboarding, and transfer requests are raised by HR."}
            action={
              roles.isHRAdmin ? (
                <Link to="/requests/new" className={styles.createBtn}>
                  + New Request
                </Link>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <Card padding={false}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Department</th>
                  <th>Effective Date</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(req => (
                  <tr
                    key={req.requestId}
                    className={styles.tableRow}
                    onClick={() => navigate(`/requests/${req.requestId}`)}
                  >
                    <td>
                      <div className={styles.employeeCell}>
                        <span className={styles.employeeName}>{req.fullName}</span>
                        <span className={styles.employeeId}>{req.oqtEmployeeId}</span>
                      </div>
                    </td>
                    <td>
                      <span className={styles.typeBadge}>
                        {req.requestType === 'Onboarding' ? '🟢' : req.requestType === 'Transfer' ? '🔄' : '🔴'} {req.requestType === 'Transfer' ? 'Dept Transfer' : req.requestType}
                      </span>
                    </td>
                    <td><StatusBadge status={req.status} /></td>
                    <td className={styles.muted}>{req.department}</td>
                    <td className={styles.muted}>
                      {req.effectiveDate && !req.effectiveDate.startsWith('0001')
                        ? new Date(req.effectiveDate).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className={styles.muted}>
                      {new Date(req.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
